import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ListVideo,
  MoreVertical,
  Trash2,
  Edit,
  Play,
  Clock,
  Image,
  Repeat,
  Shuffle,
  PlayCircle,
} from 'lucide-react';
import type { Playlist } from '@/hooks/signage';

interface PlaylistCardProps {
  playlist: Playlist;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: (active: boolean) => void;
}

export function PlaylistCard({
  playlist,
  onEdit,
  onDelete,
  onToggleActive,
}: PlaylistCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (secs === 0) return `${mins}m`;
    return `${mins}m ${secs}s`;
  };

  const getLoopModeIcon = () => {
    switch (playlist.loop_mode) {
      case 'shuffle':
        return <Shuffle className="w-3 h-3" />;
      case 'once':
        return <PlayCircle className="w-3 h-3" />;
      default:
        return <Repeat className="w-3 h-3" />;
    }
  };

  const getLoopModeLabel = () => {
    switch (playlist.loop_mode) {
      case 'shuffle':
        return 'Shuffle';
      case 'once':
        return 'Play once';
      default:
        return 'Loop';
    }
  };

  return (
    <>
      <Card className={!playlist.is_active ? 'opacity-60' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <ListVideo className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">{playlist.name}</CardTitle>
                {playlist.description && (
                  <CardDescription className="text-xs line-clamp-1">
                    {playlist.description}
                  </CardDescription>
                )}
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Playlist
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <Play className="w-4 h-4 mr-2" />
                  Preview
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stats */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Image className="w-4 h-4" />
              <span>{playlist.item_count || 0} items</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{formatDuration(playlist.total_duration || 0)}</span>
            </div>
          </div>

          {/* Playback settings */}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {getLoopModeIcon()}
              <span className="ml-1">{getLoopModeLabel()}</span>
            </Badge>
            <Badge variant="outline" className="text-xs capitalize">
              {playlist.transition_type} transition
            </Badge>
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-sm text-muted-foreground">Active</span>
            <Switch
              checked={playlist.is_active}
              onCheckedChange={onToggleActive}
            />
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Playlist</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{playlist.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
